import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Redirect } from 'react-router';
import _ from 'lodash';
import { Formik } from 'formik';

import { Section, Text } from '@tradingblock/components';
import { useLocalStorage } from '@tradingblock/storage';
import { useDispatcher } from '../components/hooks';
import { ApplicationActions } from '../state/actions';
import { useStore } from '../context';
import { useData } from '../state/selectors';
import { PageTitle, ApplicationLoader } from '../components';
import {
  DefaultApplicationModel,
  ApplicationStep,
  ApplicationType,
  TotalSecurityChallenges,
  BooleanToggleType,
} from '../types';
import { getApplicationStepUrl, updateApplication, getAffiliateHeardAboutUs } from '../services';
import { getFullUrl } from '../utils';
import config from '../config';

import { ApplicationModel } from '../types';
import styles from './HomePagePolish.module.css';
import PersonalInfoStep from '../components/application/form-steps/PersonalInfoStep';
import LoginSecurityStep from '../components/application/form-steps/LoginSecurityStep';
import Stepper from '../components/application/Stepper';
import FormNavigation from '../components/application/FormNavigation';
import MarketingImage from '../components/application/MarketingImage';
import useFormValidation from '../hooks/useFormValidation';
import useFormHelpers from '../hooks/useFormHelpers';
import { useApplicationEffects } from '../hooks/useApplicationEffects';
import LoadingSpinner from '../components/application/LoadingSpinner';
import { Errors } from '../components/Errors';

const STEPS = ['Name & Email', 'Login & Security'];

const STEP_FIELD_KEYS = [
  [
    'primaryAccountHolder.firstName',
    'primaryAccountHolder.lastName',
    'primaryAccountHolder.email',
    'primaryAccountHolder.emailConfirm',
  ],
  [
    'userName',
    'password',
    'passwordConfirm',
    ...Array.from({ length: TotalSecurityChallenges }).flatMap((_, i) => [
      `securityChallenges[${i}].securityQuestionType`,
      `securityChallenges[${i}].answer`,
    ]),
  ],
] as const;

const STEP_COMPLETION_DELAY = 1500;
const MIN_FORM_WIDTH = 340;
const MIN_FORM_HEIGHT = 400;

const HomePage: React.FC = () => {
  const isRnWebView = window.isRnWebView;
  const contentWrapRef = React.useRef<HTMLDivElement>(null);
  const [lockedHeight, setLockedHeight] = React.useState<number | null>(null);

  const storage = useStore();
  const dispatch = useDispatcher();

  const authToken = useData((d) => d.ui.apiToken);
  const storageToken = useData((d) => d.ui.storageToken);
  const application = useData((d) => d.data.application);

  // Ensure tradeAuthorization defaults to "No" if not set
  const initialFormValues = useMemo(() => {
    if (application && !_.isEmpty(application)) {
      return {
        ...application,
        ...(application.tradeAuthorization === undefined || application.tradeAuthorization === null
          ? { tradeAuthorization: BooleanToggleType.No }
          : {}),
      };
    }
    return DefaultApplicationModel;
  }, [application]);
  const { isSaving, saveType } = useData((d) => d.data.saveInfo);
  const createdUser = useData((d) => d.data.createdUser);
  const errorCode = useData((d) => d.data.createErrorCode);
  const errorMessages = useData((d) => d.data.createErrorMessages);
  const siteGrp = process.env.REACT_APP_TB_SITE_GROUP;
  const affiliate = useData((d) => d.ui.affiliate);
  const userRequirements = useData((d) => d.ui.userRequirements);

  // Store the submitted form data to use after user creation
  const [submittedFormData, setSubmittedFormData] = React.useState<ApplicationModel | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepCompleted, setStepCompleted] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false);
  const [isShowingSuccess, setIsShowingSuccess] = React.useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [scrollToErrors, setScrollToErrors] = useState(false);

  // Get localStorage instance at component level
  const localStorage = useLocalStorage();

  // Ref for error message scrolling
  const errorMessageRef = React.useRef<HTMLDivElement>(null);
  const formCardRef = React.useRef<HTMLDivElement>(null);

  const showReset = authToken && /localhost/.test(window.location.host);

  // Use validation hooks
  const { validateUserCredentials, validateStep } = useFormValidation();
  const { getSecurityQuestionTypes } = useFormHelpers();

  // Memoize expensive calculations
  const isNewOrContinue = useMemo(() => {
    return (
      !application ||
      (!(application.accountId && application.type === ApplicationType.Entity) &&
        !application.isPartialEntitySubmission) ||
      _.isEmpty(application)
    );
  }, [application]);

  const isSubmitted = useMemo(() => {
    return application && !_.isNil(application.applicationSentOn);
  }, [application]);

  const isCheckStatus = useMemo(() => {
    return Boolean(
      application &&
        (!_.isNil(application.accountId) ||
          (application.type === ApplicationType.Entity && application.isPartialEntitySubmission))
    );
  }, [application]);

  const siteName = useMemo(() => {
    return siteGrp === 'mb' ? 'MoneyBlock' : 'TradingBlock';
  }, [siteGrp]);

  // Memoize step field keys to avoid recalculation
  const currentStepFieldKeys = useMemo(() => {
    return STEP_FIELD_KEYS[currentStep] || [];
  }, [currentStep]);

  // Handle step completion
  const handleStepComplete = useCallback(() => {
    setStepCompleted(true);
    setTimeout(() => {
      setStepCompleted(false);
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    }, STEP_COMPLETION_DELAY);
  }, [currentStep]);

  const onResetApplication = useCallback(() => {
    console.warn('HomePage :: clearing application...');
    const emptyApplication = { ..._.pick(application, 'objectKey'), ...DefaultApplicationModel };
    dispatch(ApplicationActions.requestSaveApplication({ saveType: 'unload', application: emptyApplication, storage }));
  }, [application, dispatch, storage]);

  React.useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.classList.add('home-page');
    }

    return () => {
      if (mainElement) {
        mainElement.classList.remove('home-page');
      }
    };
  }, []);

  // Handle CreateUser response
  useApplicationEffects({
    createdUser,
    storageToken,
    isSaving,
    application,
    submittedFormData,
    affiliate,
    dispatch,
    storage,
    localStorage,
    setSubmittedFormData,
    setShowSuccessMessage,
    setIsShowingSuccess,
  });

  // Handle save application response and redirect to next step
  React.useEffect(() => {
    if (!isSaving && saveType === 'continue' && storageToken) {
      // Application saved successfully, redirect to the SecureAccount step to complete UserInfoSection
      let secureAccountUrl = getApplicationStepUrl(ApplicationStep.SecureAccount, siteGrp);

      // Preserve affiliate parameter in the redirect URL
      if (affiliate) {
        const separator = secureAccountUrl.includes('?') ? '&' : '?';
        secureAccountUrl += `${separator}affiliate=${affiliate}`;
      }

      // Use getFullUrl for full page reloads to include branch prefix.
      window.location.href = getFullUrl(secureAccountUrl);
    }
  }, [isSaving, saveType, storageToken, siteGrp, affiliate]);

  // Auto-scroll to error message when it first appears (only once)
  React.useEffect(() => {
    if (errorCode && errorMessages && errorMessageRef.current) {
      // Only scroll if the error is new (not already visible)
      const errorElement = errorMessageRef.current;
      const rect = errorElement.getBoundingClientRect();
      const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

      if (!isVisible) {
        // Add a small delay to ensure the error message is rendered
        setTimeout(() => {
          errorMessageRef.current &&
            errorMessageRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center', // More prominent - center the error in the viewport
            });
        }, 100);
      }
    }
  }, [errorCode, errorMessages]);

  // Scroll to top when transitioning between steps or when showing success after credentials
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep, isShowingSuccess]);

  // Scroll to first validation error when user submits with invalid data
  React.useEffect(() => {
    if (scrollToErrors && formCardRef.current) {
      const firstError = formCardRef.current.querySelector(
        '.flabel.invalid, .invalid, .error, [role="alert"]'
      ) as HTMLElement | null;
      if (firstError) {
        setTimeout(() => {
          try {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch (err) {
            console.warn('[HomePage] Scroll to validation error failed:', err);
          }
        }, 100);
      }
      setScrollToErrors(false);
    }
  }, [scrollToErrors]);

  // Clear error when user tries to submit again
  const clearErrorOnSubmit = useCallback(() => {
    if (errorCode) {
      dispatch(
        ApplicationActions.receiveCreateUser({
          payload: undefined,
          responseCode: 0,
          errors: undefined,
        })
      );
    }
  }, [errorCode, dispatch]);

  const handleUserCredentialsSubmit = useCallback(
    (values: ApplicationModel) => {
      // Handle affiliate data if present
      let applicationDataToSubmit = values;
      if (affiliate) {
        const affiliateValue = getAffiliateHeardAboutUs(affiliate);
        if (affiliateValue && !values.hearAboutUsId && !values.repId) {
          let repId = affiliateValue.repId;

          // ESTI/ESTP Specific Rep IDs due to Multiple Representatives
          if (affiliate === 'ESTI') {
            repId = affiliateValue.representatives[0].repId;
          } else if (affiliate === 'ESTP') {
            repId = affiliateValue.representatives[1].repId;
          }

          applicationDataToSubmit = {
            ...values,
            hearAboutUsId: affiliateValue.heardAboutId,
            repId: repId,
          };
        }
      }

      // Store the submitted form data for use after user creation
      setSubmittedFormData(applicationDataToSubmit);

      // Only dispatch the create user action
      // The application will be saved after user creation succeeds
      dispatch(ApplicationActions.requestCreateUser({ application: applicationDataToSubmit }));
    },
    [affiliate, dispatch]
  );

  if (createdUser && config.isVirtual) {
    return <Redirect to="/thanks" />;
  }

  if (isCheckStatus) {
    return <Redirect to="/status" />;
  }

  return (
    <>
      <ApplicationLoader allowAnonymous empty />
      <PageTitle page="home" />
      {/* Skip to content link for accessibility */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      <Section className="home-title-section" id="main-content">
        <div className="container-fluid user-creation-section">
          <div className="row d-flex align-items-center justify-content-center">
            {/* Image column - first on mobile, second on desktop */}
            {/* <div className="col-12 col-lg-6 d-flex align-items-center justify-content-center h-100 order-1 order-lg-2">
              <MarketingImage styles={styles} />
            </div> */}
            {/* Form column - second on mobile, first on desktop */}
            <div className="col-12 d-flex align-items-center justify-content-center h-100 order-2 order-lg-1">
              <div
                ref={formCardRef}
                className={styles.formCard + ' card shadow-sm w-100'}
                style={{
                  minWidth: MIN_FORM_WIDTH,
                  minHeight: MIN_FORM_HEIGHT,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  height: 'fit-content' /* Ensure consistent height */,
                }}
              >
                {!authToken && !isShowingSuccess ? (
                  // Show user creation form for new users
                  <>
                    {/* Stepper indicator and Formik form go here (no changes to their markup) */}
                    <div className={styles.formHeader}>
                      <h1>
                        <Text
                          id={currentStep === 0 ? 'getStarted' : 'createAccount.loginCredentials.title'}
                          page="home"
                        />
                      </h1>
                      {currentStep === 0 && (
                        <p className="mb-1">
                          <Text id="getStartedDescription" page="home" data={{ siteName }} />
                        </p>
                      )}
                    </div>
                    <Formik
                      initialValues={initialFormValues}
                      onSubmit={handleUserCredentialsSubmit}
                      validate={(values) => validateStep(values, currentStep, userRequirements)}
                      enableReinitialize={true}
                      validateOnBlur={false}
                    >
                      {({
                        values,
                        setValues,
                        errors,
                        touched,
                        handleSubmit,
                        validateForm,
                        isSubmitting,
                        setTouched,
                      }) => {
                        // Helper for security question types (must be inside Formik render to access values)
                        const getSecurityQuestionTypesForValues = (index: number) => {
                          return getSecurityQuestionTypes(values, index);
                        };

                        return (
                          <form
                            role="form"
                            aria-label="Account creation form"
                            onSubmit={(e) => {
                              e.preventDefault();
                              validateForm().then((stepErrors) => {
                                const keys = STEP_FIELD_KEYS[currentStep];
                                const errorMap = stepErrors as Record<string, any>;
                                const hasStepErrors = keys.some((key: string) => errorMap[key]);
                                if (!hasStepErrors && currentStep < STEPS.length - 1) {
                                  setCurrentStep(currentStep + 1);
                                } else if (hasStepErrors) {
                                  const touchedFields: Record<string, boolean> = {};
                                  keys.forEach((key) => {
                                    touchedFields[key] = true;
                                  });
                                  setTouched(touchedFields, true);
                                  setHasAttemptedSubmit(true);
                                  setScrollToErrors(true);
                                } else {
                                  // For the final step, validate the entire form before submitting
                                  const finalErrors = validateUserCredentials(values, userRequirements);
                                  if (Object.keys(finalErrors).length === 0) {
                                    // Clear any existing errors before submitting
                                    clearErrorOnSubmit();

                                    // Show success message before submitting
                                    setStepCompleted(true);

                                    // Submit after a brief delay to allow success message to be visible
                                    setTimeout(() => {
                                      handleSubmit();
                                    }, 500);
                                  } else {
                                    setTouched(finalErrors, true);
                                    setHasAttemptedSubmit(true);
                                    setScrollToErrors(true);
                                  }
                                }
                              });
                            }}
                          >
                            <div className={styles.stepContainer}>
                              <div className={`${styles.stepContent} ${currentStep === 0 ? styles.active : ''}`}>
                                <PersonalInfoStep errors={errors} touched={touched} stepCompleted={stepCompleted} />
                              </div>
                              <div className={`${styles.stepContent} ${currentStep === 1 ? styles.active : ''}`}>
                                <LoginSecurityStep
                                  errors={errors}
                                  touched={touched}
                                  values={values}
                                  stepCompleted={stepCompleted || showSuccessMessage}
                                  getSecurityQuestionTypes={getSecurityQuestionTypesForValues}
                                  TotalSecurityChallenges={TotalSecurityChallenges}
                                  userRequirements={userRequirements}
                                  hasAttemptedSubmit={hasAttemptedSubmit}
                                />
                              </div>
                            </div>
                            <FormNavigation
                              currentStep={currentStep}
                              steps={STEPS}
                              setCurrentStep={setCurrentStep}
                              isSubmitting={isSubmitting && !errorCode}
                              handleStepComplete={handleStepComplete}
                              styles={styles}
                              handleSubmit={handleSubmit}
                              showContinueLink={!authToken && !isRnWebView && isNewOrContinue && !isSubmitted}
                              siteGrp={siteGrp}
                            />
                          </form>
                        );
                      }}
                    </Formik>

                    {/* Display server errors */}
                    {errorCode && !isSaving && (
                      <div ref={errorMessageRef} className="mt-3">
                        <div className={styles.formError} role="alert" aria-live="polite">
                          <span>
                            {errorMessages && errorMessages.length > 0
                              ? errorMessages[0]
                              : 'There was a problem creating your user account. Please try again.'}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                ) : isShowingSuccess ? (
                  // Show success message next to marketing image
                  <>
                    <div className={styles.formHeader}>
                      <h1>
                        <Text id="getStarted" page="home" />
                      </h1>
                      <p className="mb-1">
                        <Text id="getStartedDescription" page="home" data={{ siteName }} />
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="alert alert-success" role="alert">
                        <i className="fas fa-check-circle me-2"></i>
                        <Text id="loginCredentialsCompletedSuccessfully" page="home" />
                      </div>
                      <div className="d-flex align-items-center justify-content-center mt-3" aria-live="polite">
                        <div
                          className="spinner-border text-primary spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></div>
                        <p className="text-muted mb-0">
                          <Text id="redirectingToApplication" page="home" />
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  // Show welcome message and buttons for authenticated users
                  <>
                    <div className={styles.formHeader}>
                      <h1>
                        <Text id="welcomeBack" page="home" />
                      </h1>
                      <p>
                        <Text id="welcomeBackDescription" page="home" />
                      </p>
                    </div>

                    <div className="button-container d-flex flex-column gap-3">
                      {isNewOrContinue && !isSubmitted && authToken && (
                        <Link
                          className="btn-unified btn-unified--primary btn-unified--full"
                          to={getApplicationStepUrl(
                            authToken && !_.isEmpty(application) ? 'summary' : ApplicationStep.SecureAccount,
                            siteGrp
                          )}
                          aria-label={authToken ? 'Continue your application' : 'Start a new application'}
                        >
                          <Text id={authToken ? 'continue' : 'startNewApplication'} page="home" />
                        </Link>
                      )}
                      {showReset && (
                        <button
                          className="btn-unified btn-unified--primary btn-unified--full"
                          type="button"
                          onClick={onResetApplication}
                          aria-label="Reset your application"
                        >
                          <Text id="resetApplication" page="home" />
                        </button>
                      )}
                      {authToken && !isRnWebView && (
                        <Link
                          className="btn-unified btn-unified--secondary btn-unified--full"
                          to="/logout"
                          aria-label="Sign out of your account"
                        >
                          <Text id="btns.sign-out" />
                        </Link>
                      )}
                      {!isSaving && saveType === 'unload' && showReset && (
                        <div className="alert alert-success mb-0" role="alert">
                          <i className="fas fa-check-circle me-2" aria-hidden="true"></i>
                          <Text id="applicationResetSuccess" page="home" />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default HomePage;
